// functions/makeStatement.ts
import { store }           from '../index';
import { SET_LRS_STATE }   from '../actions/eventActions';
import makUuid             from './make_uuid';
import { DateTime, Interval, Duration } from 'luxon';

const ABS_PREFIX = 'https://www.yisumatica.org.il/new/';

const isAbsoluteUrl = (s?: string) => !!s && /^https?:\/\//i.test(s.trim());

/** Build ISO-8601 duration, always starting with "PT" */
const buildDuration = (ms: number) => {
  const duration = Duration.fromMillis(ms).toFormat('hh:mm:ss'); // "HH:MM:SS"
  const rand = `${Math.random().toFixed(2)}`.slice(1);           // ".xy"
  const H = duration.slice(0, 2);
  const M = duration.slice(3, 5);
  const S = duration.slice(6);

  let dur = 'PT';
  if (+H !== 0) dur += `${+H}H`;
  if (+M !== 0 || +H !== 0) dur += `${+M}M`;
  dur += `${+S}${rand}S`;
  return dur;
};

/** Hash identity number for MoE (SHA-256) */
const hashIdentityNumber = async (value: string): Promise<string> => {
  // Always hash the value (whether it's an ID number or email) for MoE compliance
  // MoE requires hashed identity, not plain text
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

const LMS_ACTIVITY_ID = 'https://www.yisumatica.org.il';
const LMS_ACTIVITY_TYPE = 'https://lxp.education.gov.il/xapi/moe/lms';
const COURSE_ACTIVITY_TYPE = 'https://lxp.education.gov.il/xapi/moe/activities/course';
const ECAT_BASE = 'https://lxp.education.gov.il/xapi/moe/ecat/item/';

const makeStatement = async (
  appId:    string,
  appName:  string,
  appTopic: string,
  userId:   string,
  verb:     string,
  tag:      string,
  iri:      string
) => {
  const now       = DateTime.local();
  const state     = store.getState();
  const entryTime = state.lrs.entryTime;

  const intrvlMs  = Interval.fromDateTimes(entryTime, now).count('milliseconds');
  const uuid      = makUuid();
  const time      = `${now.toFormat('yyyy-LL-dd')}T${now.toISOTime()}`;
  const dur       = buildDuration(intrvlMs);

  // Update entry time for the next measurement cycle
  store.dispatch(SET_LRS_STATE({ entryTime: now }));

  // Resolve the Activity ID:
  // - If appId is absolute → use as-is
  // - Else → prefix with '/new/' as in legacy behavior
  const activityId = isAbsoluteUrl(appId) ? appId : `${ABS_PREFIX}${appId}`;

  // Helper: Determine MoE identity type from userId
  const getIdentityHomePage = (uid: string): string => {
    const isNumericId = /^\d+$/.test(uid);
    return isNumericId
      ? 'https://lxp.education.gov.il/xapi/moe/identity/idnumber'
      : 'https://lxp.education.gov.il/xapi/moe/identity/exidentifier';
  };

  // Determine identity type
  const identityHomePage = getIdentityHomePage(userId);
  const identityType = /^\d+$/.test(userId) ? 'idnumber' : 'exidentifier';

  // Hash identity number if it's numeric (Israeli ID number)
  const hashedIdentity = await hashIdentityNumber(userId);

  // Tag type (for category)
  const tagType = 'https://lxp.education.gov.il/xapi/moe/tag';

  const tagValueRaw = (tag ?? '').toString().trim();
  let courseActivityId = '';
  if (tagValueRaw.startsWith('http')) {
    courseActivityId = tagValueRaw;
  } else if (/^\d+$/.test(tagValueRaw)) {
    const padded = tagValueRaw.padStart(5, '0');
    courseActivityId = `${ECAT_BASE}${padded}`;
  }

  // ---- TRACE LOGS ----
  console.groupCollapsed('%c[LRS-ID] makeStatement', 'color:#10b981;font-weight:700');
  console.log('incoming appId :', appId);
  console.log('isAbsolute     :', isAbsoluteUrl(appId));
  console.log('activityId     :', activityId);
  console.log('duration (ISO) :', dur);
  console.log('identity type  :', identityType);
  console.log('actor homePage :', identityHomePage);
  console.log('hashed identity:', hashedIdentity);
  console.groupEnd();
  // --------------------

  let statement: { [key: string]: any }[] = [
    {
      id: uuid,
      actor: {
        objectType: 'Agent',
        account: {
          homePage: identityHomePage, // Fixed: MoE identity (idnumber or exidentifier)
          name: hashedIdentity // Fixed: hashed identity number instead of email
        }
      },
      verb: {
        id: `https://lxp.education.gov.il/xapi/moe/verbs/${verb === 'entered' ? 'enter' : verb}` // Fixed: normalize 'entered' to 'enter' per MoE vocabulary
      },
      object: {
        objectType: 'Activity',
        id: activityId, // ← fixed: no double "/new/https://…"
        definition: {
          type: 'https://lxp.education.gov.il/xapi/moe/application',
          name: { he: appName },
          description: { he: appTopic }
        }
      },
      context: {
        contextActivities: {
          grouping: [
            {
              objectType: 'Activity',
              id: LMS_ACTIVITY_ID,
              definition: {
                type: LMS_ACTIVITY_TYPE,
                name: { he: 'Yisumatica LMS' }
              }
            },
            ...(courseActivityId
              ? [{
                  objectType: 'Activity',
                  id: courseActivityId,
                  definition: {
                    type: COURSE_ACTIVITY_TYPE,
                    name: { he: appName }
                  }
                }]
              : [])
          ],
          category: [
            {
              objectType: 'Activity',
              id: iri,
              definition: {
                type: tagType // Fixed: changed from application to tag type
              }
            }
          ]
        },
        revision: '1.0',
        language: 'he'
      },
      timestamp: time,
      version: '1.0.3'
    }
  ];

  // If the verb is "played", add a result.duration field
  if (verb === 'played') {
    const obj = statement[0];
    const keyValues = Object.entries(obj);
    keyValues.splice(4, 0, [
      'result',
      { duration: dur }
    ]);
    statement[0] = Object.fromEntries(keyValues);
  }

  console.info('[LRS-ID] built statement object.id =', statement[0]?.object?.id);
  console.log(JSON.stringify(statement, null, 2));

  // Backend expects a JSON stringified array
  return JSON.stringify(statement);
};

export default makeStatement;

// "id": "https://cad.lxp.eduction.gov.il/xapi/verbs/"+verb
